import React, { useCallback, useEffect, useState } from 'react';

// assets
import { ICONS } from '../../../../../../assets/icons';
import Tick from '../../../../../../assets/customSVG/Tick';
import Xmark from '../../../../../../assets/customSVG/Xmark';

// api
import { generateQR, updateInitialTrigger } from '../../../../api/sequence';
import { whatsappBusinessNumbers } from '../../../../../../api/Api';

//redux
import { updateToggleToast } from '../../../../../../reduxToolkit/appSlice';
import {
  useAspDispatch,
  useAspSelector,
} from '../../../../../../test/jest-redux-hooks';

// helpers
import { cn } from 'helper/cn';
import { downloadImageFromUrl } from 'helper/fileSaver';
import { copyText } from 'helper/text';

// hooks
import useToggle from '../../../../../../hooks/useToggle';

// components
import Icons from '../../Icons';
import Loader from '../../../../../../components/commonComponents/Loader/Index';
import { Popover } from 'antd';
import { useParams } from 'react-router-dom';
import Link from '../../../../../../assets/customSVG/sequence/Link';

// const daily_messaging_limit = {
//   TIER_50: 50,
//   TIER_100: 100,
//   TIER_250: 250,
//   TIER_1K: 1000,
//   TIER_10K: 10000,
//   TIER_100K: 100000,
//   TIER_UNLIMITED: 100000000,
// };

const daily_messaging_limit = {
  TIER_50: '50',
  TIER_250: '250',
  TIER_1K: '1k',
  TIER_10K: '10k',
  TIER_100K: '100k',
  TIER_UNLIMITED: 'Unlimited',
};

export default function QrCodeTrigger({
  node,
  originalData,
  onChange,
  toggleNodeEditMode,
  allowToEditFlow,
  removeEdges,
  sequenceDetails,
}) {
  const { id, uniq_id, trigger_type, data, settings } = node;

  const { sequence_id, campaign_id } = useParams();
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();

  const [isTriggerExist, setIsTriggerExist] = useToggle(false);
  const [isLoading, setIsLoading] = useToggle(false);
  const [isGeneratingQR, setIsGeneratingQR] = useToggle(false);
  const [isUpdating, setIsUpdating] = useToggle(false);
  const [isCopied, setIsCopied] = useToggle(false);
  const [updatedNode, setUpdatedNode] = useState(null);

  const [isOpen, setIsOpen] = useToggle(false);
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!updatedNode) setUpdatedNode(originalData);
  }, [originalData]);

  useEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(() => setIsCopied(false), 5000);

      return () => clearTimeout(timeout);
    }
  }, [isCopied]);

  useEffect(() => {
    if (currentBrand) {
      getwhatsappBusinessNumbers(currentBrand?.brand_id);
    }
  }, [currentBrand]);

  const getwhatsappBusinessNumbers = async (brand_id) => {
    setIsLoading(true);
    try {
      const res = await whatsappBusinessNumbers(brand_id);
      setList(res?.data?.wapp_business_num);
      count = res?.data?.wapp_business_num_count;
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const isTriggerTextChanged = useCallback(
    (value = null) => {
      if (updatedNode?.data?.url_text?.length === 0) return false;

      value = value ?? data?.url_text;

      return updatedNode?.data?.url_text?.trim() !== value.trim();
    },
    [data, updatedNode]
  );

  const isOptionSelected = (option) => {
    return option?.id === data?.sendFrom?.id;
  };

  const generateQRcode = async () => {
    setIsGeneratingQR(true);
    try {
      const payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id,
        phone_number: data?.phone_number,
        url_text: data?.url_text.trim(),
      };

      const res = await generateQR(sequence_id, payload);

      if (res?.status === 200) {
        removeEdges(node?.uniq_id);
        const updatedData = {
          ...data,
          url: res?.data?.url,
          QR: res?.data?.qr_url,
          url_text: data?.url_text.trim(),
        };
        onChange({
          type: 'UPDATE_NODE',
          id: uniq_id,
          key: 'data',
          data: updatedData,
        });
        setUpdatedNode({
          ...originalData,
          data: updatedData,
        });
      } else {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: res?.response?.data?.message,
              status: 'Error',
              duration: '',
            },
          ])
        );
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const isValidateSendPhoneNumber = useCallback(() => {
    const isValidPhoneNumber =
      data?.phone_number?.send_from_phone_number?.length > 0;
    const isValidPhoneNumberName =
      data?.phone_number?.send_from_phone_name?.length > 0;
    const isValidPhoneNumberId =
      data?.phone_number?.send_from_phone_number_id?.length > 0;
    const isValidPhoneNumberCountryCode =
      data?.phone_number?.country_code?.length > 0;

    const isValidWhatsAppNumber =
      isValidPhoneNumberId &&
      isValidPhoneNumberName &&
      isValidPhoneNumber &&
      isValidPhoneNumberCountryCode;
    return isValidWhatsAppNumber;
  }, [data]);

  const showGenerateQR = useCallback(() => {
    if (updatedNode?.data?.url_text?.length === 0 && data?.url_text?.length > 0)
      return true;

    if (!isTriggerTextChanged()) return false;

    const isValidCustomerMessage = data?.url_text?.length > 0;

    const isValidPhoneNumber = isValidateSendPhoneNumber();

    return isValidCustomerMessage && isValidPhoneNumber;
  }, [data, updatedNode, isValidateSendPhoneNumber]);

  const showSubmit = useCallback(() => {
    // const isAllowToGenerateQR = showGenerateQR();

    const isValidPhoneNumber = isValidateSendPhoneNumber();
    const isValidQR = data?.QR?.length > 0;
    const isValidUrl = data?.url?.length > 0;
    const isValidUrlText = data?.url_text?.length > 0;

    return isValidPhoneNumber && isValidQR && isValidUrl && isValidUrlText;
  }, [data]);

  const handleYes = async () => {
    setIsUpdating(true);
    setIsTriggerExist(true);

    try {
      const allowToSubmit = showSubmit();
      const isUpdated =
        originalData?.data?.url_text?.length > 0
          ? data?.url_text !== originalData?.data?.url_text
          : true;

      if (!isUpdated && allowToSubmit) {
        toggleNodeEditMode({
          id: node?.id,
          uniqId: node?.uniq_id,
          value: 'false',
          defaults: node?.settings?.defaults,
        });
      } else {
        try {
          let payload = {
            brand_id: currentBrand?.brand_id,
            campaign_id,
            // sequence_id,
          };

          if (allowToSubmit) {
            payload = {
              ...payload,
              trigger_type: trigger_type?.toLowerCase(),
              save: true,
              details: {
                phone_number: data?.phone_number,
                url_text: data?.url_text,
                url: data?.url,
                qr_url: data?.QR,
              },
              remove_edges: true,
            };

            const res = await updateInitialTrigger(sequence_id, node?.uniq_id, {
              ...payload,
              defaults: node?.settings?.defaults,
            });

            if (res?.status === 200) {
              onChange({
                type: 'UPDATE_NODE_DATA',
                id: node?.uniq_id,
                data: res?.data?.data?.[0],
              });

              onChange({
                type: 'UPDATE_DATA',
                data: { selectedNode: null },
              });
            } else {
              setIsTriggerExist(true);
              dispatch(
                updateToggleToast([
                  ...toggleToast,
                  {
                    id: toggleToast?.length + 1,
                    content: res?.response?.data?.error,
                    status: 'Error',
                    duration: '',
                  },
                ])
              );
            }
          }

          setIsUpdating(false);
        } catch (error) {
          console.warn(error);
        }
      }
    } catch (error) {
      console.warn(error);
    }
  };

  return (
    <div className='w-full flex-1 min-w-full max-w-[17rem] h-full rounded-2xl overflow-hidden select-none'>
      <div
        className={cn(
          'flex items-center justify-between gap-5 px-5 py-2.5 bg-[#04B8A2] text-white'
        )}>
        <div className='flex items-center gap-5'>
          <Icons
            keyProp={node?.label?.toLowerCase()}
            bgProp={'transparent'}
          />
          <p className='capitalize'>{node?.label}</p>
        </div>
        <div
          className='size-4 flex items-center justify-center cursor-pointer'
          onClick={() => {
            toggleNodeEditMode({
              id: node?.id,
              uniqId: node?.uniq_id,
              value: 'false',
              defaults: node?.settings?.defaults,
            });
            onChange({
              type: 'UPDATE_NODE',
              id: node?.uniq_id,
              key: 'data',
              data: node?.status === 'NEW' ? null : updatedNode?.data,
            });
          }}>
          <Xmark
            color='white'
            width={12}
            height={12}
          />
        </div>
      </div>

      <div className='w-full px-7 py-5 flex flex-col gap-4'>
        <p className='text-sm weight-medium'>
          Choose the WhatsApp business number you'd like to use for your QR
          code/Link{' '}
        </p>

        <div className='flex flex-col gap-1.5 pt-5 pb-2.5'>
          <h5 className='text-sm weight-semibold text-[var(--font-600)]'>
            WhatsApp Number
          </h5>
          <Popover
            trigger={'click'}
            open={isOpen}
            onOpenChange={(open) => setIsOpen(open)}
            placement='bottomLeft'
            arrow={false}
            content={
              <div className='w-full min-w-[24.5rem] bg-[var(--white)] rounded-2xl z-50 p-2.5'>
                {isLoading ? (
                  <div className='w-full h-16 flex flex-1 items-center justify-center rounded-2xl cursor-pointer'>
                    <Loader
                      Width={25}
                      Height={25}
                    />
                  </div>
                ) : (
                  <ul
                    className={`w-full m-0 list-none flex-col gap-1.5 overflow-y-scroll listScroll ${
                      isOpen ? 'flex' : 'hidden'
                    }`}>
                    {list?.map((option) => {
                      return (
                        <li
                          key={option?.id}
                          className='w-full h-[80px] flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border-100)] last:border-0 cursor-pointer'
                          onClick={() => {
                            setIsOpen(false);

                            onChange({
                              type: 'UPDATE_NODE',
                              id: node?.uniq_id,
                              primaryKey: 'data',
                              key: 'phone_number',
                              data: {
                                id: option?.id,
                                country_code: option?.country_code,
                                send_from_phone_name:
                                  option?.send_from_phone_name,
                                send_from_phone_number:
                                  option?.send_from_phone_number,
                                send_from_phone_number_id:
                                  option?.send_from_phone_number_id,
                              },
                            });
                          }}>
                          <div className='flex flex-col gap-2.5'>
                            <div className='flex items-center justify-between gap-5'>
                              <p>{option?.send_from_phone_name}</p>
                            </div>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-1.5 text-sm weight-medium text-[#616874] pr-3.5'>
                                {option?.country_code}
                                {option?.send_from_phone_number}
                              </div>
                              <div className='flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)]  border-x border-[var(--border-100)] px-3.5'>
                                <p className='size-2.5 bg-[var(--primary)] rounded-sm'></p>
                                <p>High</p>
                              </div>
                              <div className='flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)] px-3.5'>
                                {
                                  daily_messaging_limit[
                                    option?.daily_messaging_limit
                                  ]
                                }
                                /day
                              </div>
                            </div>
                          </div>
                          {isOptionSelected(option) && (
                            <img
                              src={ICONS?.Selected}
                              alt='selected'
                              className='size-5'
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            }>
            <div
              className='w-full h-12 flex items-center justify-between border boder-[var(--border-50)] px-5 py-1 rounded-lg cursor-pointer'
              onClick={() => {
                setIsOpen(!isOpen);
              }}>
              {data?.phone_number?.send_from_phone_name ? (
                <div
                  className={`flex-grow flex items-center gap-2.5 text-[var(--contentText)]`}>
                  <p>{data?.phone_number?.send_from_phone_name}</p>
                  <p>
                    {data?.phone_number?.country_code}
                    {data?.phone_number?.send_from_phone_number}
                  </p>
                </div>
              ) : (
                <div className='w-full flex items-center font-md weight-medium text-[var(--font-300)]'>
                  Please select
                </div>
              )}

              {/* <div
              className={`transition-transform duration-200 ease-linear cursor-pointer ${
                isOpen ? '-rotate-180' : 'rotate-0'
              }`}>
              <div
                className={`border rounded-t-1.5 border-transparent border-t-[#777] translate-y-[25%]`}></div>
            </div> */}
              <img
                src={ICONS?.modelDropdownIcon}
                className={cn(
                  'transition-transform duration-200 ease-linear cursor-pointer',
                  isOpen ? '-rotate-180' : 'rotate-0'
                )}
              />
            </div>
          </Popover>
        </div>

        <div className='w-full contents-[""] border-b border-[var(--border-100)]'></div>

        <div className='flex flex-col gap-1.5 pt-5 pb-2.5'>
          <h5 className='text-sm weight-semibold text-[var(--font-600)]'>
            Customer Message
          </h5>
          <div
            className={cn(
              'w-full h-full px-2.5 py-2 border rounded-[10px] bg-transparent',
              isTriggerExist ? 'border-red-200' : 'border-[#E8E8EA]',
              isGeneratingQR ? 'cursor-not-allowed' : 'cursor-default'
            )}>
            <textarea
              name=''
              id=''
              value={data?.url_text}
              placeholder='E.g : Hello! Can i get more info on this?'
              className='w-full h-11 resize-none bg-transparent outline-none border-none'
              maxLength={80}
              disabled={isGeneratingQR}
              onChange={(event) => {
                const { value } = event.target;
                let payload = { ...data, url_text: value };

                if (!isTriggerTextChanged(value)) {
                  payload = {
                    ...payload,
                    QR: updatedNode?.data?.QR,
                    url: updatedNode?.data?.url,
                  };
                } else {
                  payload = { ...payload, QR: '', url: '' };
                }
                onChange({
                  type: 'UPDATE_NODE',
                  id: node?.uniq_id,
                  key: 'data',
                  data: payload,
                });
                if (isTriggerExist) setIsTriggerExist(false);
                // if (data?.QR?.length > 0 && data?.url?.length > 0)
                //   onChange({
                //     type: 'UPDATE_NODE',
                //     id: node?.uniq_id,
                //     primaryKey: 'data',
                //     data: { ...data, QR: '', url: '', url_text: value },
                //   });
              }}></textarea>
            <div className='flex items-center justify-end'>
              <p className='text-sm weight-semibold text-[#898E99]'>
                {data?.url_text?.length || 0}/80
              </p>
            </div>
          </div>
          <p className='text-[13px] weight-medium text-[var(--font-600)]'>
            Suggest questions or messages that people can send.
          </p>
        </div>

        <button
          className={cn(
            'w-28 h-8 rounded-md text-[13px] weight-semibold mx-auto flex items-center justify-center',
            !isGeneratingQR && showGenerateQR()
              ? 'bg-[#6564CA] text-white cursor-pointer'
              : 'bg-[var(--BG-100)] text-[var(--font-600)] cursor-not-allowed'
          )}
          onClick={() => {
            if (!isGeneratingQR && showGenerateQR()) generateQRcode();
          }}>
          {isGeneratingQR ? (
            <Loader
              Width={14}
              Height={14}
              loaderBg='white'
            />
          ) : (
            <>Generate Link</>
          )}
        </button>

        {!showGenerateQR() && data?.QR?.length > 0 && data?.url?.length > 0 && (
          <div className='w-full h-24 flex items-center gap-5 bg-[#F8F9FC] p-2.5 rounded-[10px]'>
            <img
              src={data?.QR}
              alt='qr code'
              className='w-24 h-full object-contain'
            />
            <div className='w-4/6 flex flex-col gap-2.5'>
              <div className='w-full h-9 flex items-center justify-between gap-2 bg-white border border-[var(--border-100)] px-1.5 py-1 rounded-md'>
                <div className='size-5 flex items-center justify-center'>
                  <Link />
                </div>
                <p className='w-44 truncate text-[13px] weight-semibold'>
                  {data?.url}
                </p>
                <button
                  className={cn(
                    'w-16 h-7 bg-[var(--primary)] text-white px-2 flex items-center justify-center rounded-md transition-all ease-linear duration-150'
                  )}
                  onClick={() => {
                    if (!isCopied && data?.url?.length > 0) {
                      copyText(data?.url);
                      setIsCopied(true);
                    }
                  }}>
                  {isCopied ? (
                    <Tick
                      width='12'
                      height='10'
                      strokeWidth='3'
                      stroke='white'
                    />
                  ) : (
                    'Copy'
                  )}
                </button>
              </div>
              <div className='flex items-center gap-5'>
                <p className='text-[13px] weight-semibold text-[var(--font-600)]'>
                  Or
                </p>
                <button
                  className='px-2.5 py-1 text-[13px] weight-semibold text-[var(--primary)] border border-[var(--primary)] rounded-md'
                  onClick={() => {
                    if (data?.QR?.length > 0) downloadImageFromUrl(data?.QR);
                  }}>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className='w-full h-fit flex justify-end px-6 py-2.5 bg-white'>
        <button
          className={cn(
            'w-14 h-8 flex items-center justify-center text-white px-4 py-1 rounded-md',
            showSubmit() && !isUpdating
              ? 'bg-[var(--primary)] cursor-pointer opacity-100'
              : 'bg-[var(--primary)] opacity-50 cursor-not-allowed'
          )}
          onClick={() => {
            if (showSubmit() && !isUpdating) handleYes();
          }}
          disabled={isUpdating}>
          {isUpdating ? (
            <Loader
              Width={14}
              Height={14}
              loaderBg='white'
            />
          ) : (
            <div className='flex items-center justify-center gap-1.5'>
              <Tick
                width='12'
                height='10'
                strokeWidth='2.5'
                stroke='white'
              />
              <span>OK</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
